import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import type { PublicPlanCatalogRow, SubscriptionPlanCatalogRow } from '../api/billing.client';
import { useTutorSystemCapabilities } from '../../../hooks/useTutorSystemCapabilities';
import { formatPlanQuotaLine } from '../../../tutor/utils/quotaRuntimeV2Display';

type PlanLike = PublicPlanCatalogRow | SubscriptionPlanCatalogRow;

export function planModules(p: PlanLike): string[] {
  const out: string[] = [];
  if (p.ai_tutor_enabled) out.push('AI Tutor');
  if (p.communication_enabled) out.push('Communication Tutor');
  if ('scenario_enabled' in p && p.scenario_enabled) out.push('Scenario Conversation');
  if ('picture_talk_enabled' in p && p.picture_talk_enabled) out.push('Picture Talk');
  if (p.story_enabled) out.push('Story Studio');
  if (p.grammar_enabled) out.push('Grammar / Writing');
  if (p.analytics_enabled) out.push('Analytics');
  if (p.parent_portal_enabled) out.push('Parent portal');
  return out;
}

type Props = {
  plan: PlanLike;
  billingCycle: 'monthly' | 'yearly';
  isCurrent?: boolean;
  highlight?: boolean;
  action?: React.ReactNode;
  showCheckoutHint?: boolean;
};

export const PlanCatalogCard: React.FC<Props> = ({
  plan,
  billingCycle,
  isCurrent,
  highlight,
  action,
  showCheckoutHint,
}) => {
  const { quotaRuntimeV2Enabled } = useTutorSystemCapabilities();
  const runtimeV2 = quotaRuntimeV2Enabled === true;
  const price =
    billingCycle === 'yearly' ? plan.annual_price_usd : plan.monthly_price_usd;
  const cycleLabel = billingCycle === 'yearly' ? '/year' : '/month';
  const modules = planModules(plan);
  const isEnterprise = plan.plan_key.toUpperCase() === 'ENTERPRISE';

  return (
    <Card
      className={
        highlight || isCurrent
          ? 'ring-2 ring-app-primary dark:ring-app-primary'
          : undefined
      }
    >
      <CardHeader>
        <CardTitle className="text-lg">{plan.plan_name}</CardTitle>
        <p className="font-mono text-xs text-slate-500">{plan.plan_key}</p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isCurrent ? (
          <p className="text-xs font-semibold text-app-primary dark:text-app-primary/80">Your current plan</p>
        ) : null}
        {plan.description ? (
          <p className="text-slate-600 dark:text-slate-400">{plan.description}</p>
        ) : null}
        {!isEnterprise ? (
          <p className="text-2xl font-bold text-app-text dark:text-white">
            ${price.toLocaleString()}
            <span className="text-sm font-normal text-slate-500">{cycleLabel}</span>
          </p>
        ) : (
          <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">Custom pricing</p>
        )}
        <p className="text-xs">
          Seats: {plan.max_students} students · {plan.max_teachers} teachers · {plan.max_parents} parents
        </p>
        <p className="text-xs">
          {formatPlanQuotaLine(runtimeV2, plan.live_daily_minutes, plan.live_monthly_minutes, {
            maxSessionMinutes: plan.max_session_minutes,
            idleTimeoutSeconds: plan.idle_timeout_seconds,
          })}
        </p>
        {modules.length ? (
          <ul className="list-disc space-y-0.5 pl-4 text-xs text-slate-700 dark:text-slate-300">
            {modules.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : null}
        {showCheckoutHint &&
        'available_cycles' in plan &&
        plan.available_cycles.length === 0 &&
        !isEnterprise ? (
          <p className="text-xs text-amber-600">Checkout coming soon for this plan.</p>
        ) : null}
        {action ? <div className="pt-2">{action}</div> : null}
      </CardContent>
    </Card>
  );
};
